/**
 * The three parts of a German plate. This shape only exists because the German input
 * renders one field per part — everywhere else a plate is just its string.
 */
export interface GermanNumberPlateFields {
  region: string;
  firstID: string;
  secondID: string;
}

/**
 * Splits a German plate string so the three input fields can be prefilled from an
 * existing value. Returns null for anything it cannot parse, which includes every
 * foreign plate — those are edited as a single string and never need splitting.
 */
export function splitGermanNumberPlate(
  numberPlate: string | null | undefined,
): GermanNumberPlateFields | null {
  /*
    //     ^                  # Start of line
    //     ([\pLu]{1,3})      # Unterscheidungszeichen (1-3 uppercase letters, including umlauts)
    //     [-\s]              # Separator, non-optional, dash or space
    //     ([\pLu]{1,2})      # Erkennungsnummer letters (1-2 uppercase letters, including umlauts)
    //     [\s\-]?            # Optional separator, dash or space
    //     (\d{1,4})          # Erkennungsnummer digits (1-4 digits)
    //     \s*                # Allow optional space before the electric or oldtimer indicator
    //     [^EH]*             # Ignore everything that is not electric or oldtimer indicator so we can find the optional E or H at the end
    //     ([EH])?            # Optional electric (E) or oldtimer (H) indicator
    //     .*                 # Ignore everything after the electric or oldtimer indicator
    //     $                  # End of line
    */
  const match = numberPlate?.match(
    /^([\p{Lu}]{1,3})[-\s]([\p{Lu}]{1,2})[\s-]?(\d{1,4})\s*[^EH]*([EH])?.*$/u,
  );

  if (!match) {
    return null;
  }

  // The first element of `match` is the full string, so we use array destructuring
  // to capture the groups we need (region, firstID, secondID in numbers and special characters (E/H)).
  const [, region, firstID, secondIDNumbers, secondIDSpecial] = match;

  return { region, firstID, secondID: secondIDNumbers + (secondIDSpecial || "") };
}

/**
 * Serializes the three German input fields into the format the backend parser expects.
 * An incomplete plate serializes to an empty string: the plate is optional, so a partial
 * value must not be mistaken for one the user actually entered.
 */
export function joinGermanNumberPlate({
  region,
  firstID,
  secondID,
}: GermanNumberPlateFields): string {
  if (!region || !firstID || !secondID) {
    return "";
  }
  return `${region}-${firstID} ${secondID}`;
}

/**
 * Mirrors app/BaseComponents/NumberPlate/Parser/FromString/Countries/Austria/Regex.php:
 *
 *   ^([A-PR-Z]{1,2})[-\s]([1-9]\d{0,4})\s?([A-PR-Z]{1,2})$
 *     Bezirkskennzeichen  Ziffernblock     Buchstabenblock
 *     1-2 letters, no Q   1-5, no leading 0  1-2 letters, no Q
 *
 * Note the order: Austrian plates put the digits *before* the letters.
 */
const AUSTRIAN_NUMBER_PLATE = /^[A-PR-Z]{1,2}[-\s][1-9]\d{0,4}\s?[A-PR-Z]{1,2}$/;

/**
 * Mirrors the German parser: Unterscheidungszeichen, Erkennungsnummer letters,
 * Erkennungsnummer digits, and the optional electric (E) / oldtimer (H) suffix.
 */
const GERMAN_NUMBER_PLATE = /^\p{Lu}{1,3}[-\s]\p{Lu}{1,2}[\s-]?\d{1,4}\s?[EH]?$/u;

const normalize = (value: string): string => value.trim().toUpperCase();

export function isAustrianNumberPlate(value: string): boolean {
  return AUSTRIAN_NUMBER_PLATE.test(normalize(value));
}

export function isGermanNumberPlate(value: string): boolean {
  return GERMAN_NUMBER_PLATE.test(normalize(value));
}

/**
 * Accepts either supported format. The backend infers the country from whichever
 * parser matches, so the string alone is enough — no country has to be transmitted.
 */
export function isValidNumberPlate(value: string): boolean {
  return isGermanNumberPlate(value) || isAustrianNumberPlate(value);
}
